<template>
  <div class="gallery">
    <div
      ref="grid"
      class="gallery__inner"
      :style="style"
      :class="{
        'no-animate': initialTouchX !== null && initialTouchX >= 30,
        'gallery__inner--as-grid': galleryAsGrid,
      }"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
    >
      <gallery-child
        v-for="(element, index) in slider.elements"
        :key="element._key"
        ref="child"
        :child="element"
        :gallery-state="galleryState"
        :index="index"
        :zoom-index="zoomImage.index"
        @zoom="onZoom"
      />
    </div>

    <transition name="from-bottom">
      <div
        v-if="showSliderUtils && currentIndex === 0"
        class="gallery__page d-flex justify-center"
      >
        <primary-button
          :text="$t('action.showAllPictures')"
          aspect="light"
          size="tertiary"
          with-arrow
          arrow-is-icon
          icon="grid"
          arrow-position="left"
          @click="galleryAsGrid = !galleryAsGrid"
        />
      </div>
    </transition>

    <slider-nav
      v-if="showSliderUtils"
      :hide-left="currentIndex === 0"
      :hide-right="currentIndex === slider.elements.length - 1"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @prev="prev"
      @next="next"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'
import galleryTools from '@/mixins/galleryTools'
import { generateGalleryState } from '@/utils/gallery'

import GalleryChild from '@/components/gallery/GalleryChild'
import SliderNav from '@/components/slider/SliderNav'
import PrimaryButton from '@/components/buttons/Primary'

export default {
  name: 'Gallery',
  components: {
    GalleryChild,
    SliderNav,
    PrimaryButton,
  },
  mixins: [galleryTools, NuxtSSRScreenSize.NuxtSSRScreenSizeMixin],
  props: {
    slider: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    gap: 0,
    gapDesktop: 0,
    initialTouchX: null,
    diffX: 0,
    currentIndex: 0,
    resizeObserver: null,
    zoomImage: {
      flip: null,
      index: -1,
    },
    galleryAsGrid: false,
  }),
  computed: {
    hasMatrix() {
      return this.slider.gallery && this.slider.gallery.length > 0
    },
    hasMatrixMobile() {
      return this.slider.gallery_mobile && this.slider.gallery_mobile.length > 0
    },
    galleryState() {
      return {
        desktop: this.hasMatrix
          ? generateGalleryState(
              this.slider.gallery,
              this.slider.elements.length
            )
          : process.env.DEFAULT_GALLERIES
          ? generateGalleryState(
              process.env.DEFAULT_GALLERIES.desktop,
              this.slider.elements.length
            )
          : null,
        mobile: this.hasMatrixMobile
          ? generateGalleryState(
              this.slider.gallery_mobile,
              this.slider.elements.length
            )
          : process.env.DEFAULT_GALLERIES
          ? generateGalleryState(
              process.env.DEFAULT_GALLERIES.mobile,
              this.slider.elements.length
            )
          : null,
      }
    },
    galleryHeight() {
      return {
        mobile: this.getGalleryGridTemplateRows(
          this.galleryState.mobile.lines,
          this.gap
        ),
        desktop: this.getGalleryGridTemplateRows(
          this.galleryState.desktop.lines,
          this.gapDesktop
        ),
      }
    },
    currentLeft() {
      return this.currentIndex * -this.galleryWidth + this.diffX
    },
    style() {
      return {
        '--lines': this.galleryHeight.mobile,
        '--gap': `${this.gap}px`,
        '--lines-desktop': this.galleryHeight.desktop,
        '--gap-desktop': `${this.gapDesktop}px`,
        '--margin-left': `${this.currentLeft}px`,
      }
    },
    showSliderUtils() {
      return (
        this.$vssWidth < 769 &&
        this.slider.elements.length > 1 &&
        !this.galleryAsGrid
      )
    },
  },
  mounted() {
    this.galleryWidth = this.$el.offsetWidth

    if (window.ResizeObserver === undefined) {
      return
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return
        }
        this.galleryWidth = this.$el.offsetWidth
      })
    })

    this.resizeObserver.observe(this.$el)
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this.zoomImage.flip) {
      this.zoomImage.flip.kill()
    }
  },
  methods: {
    ...mapMutations({
      setScroll: 'behavior/SET_SCROLL',
    }),
    prev() {
      this.currentIndex = Math.max(0, this.currentIndex - 1)
    },
    next() {
      this.currentIndex = Math.min(
        this.slider.elements.length - 1,
        this.currentIndex + 1
      )
    },
    onTouchStart(e) {
      if (this.galleryAsGrid) return
      this.initialTouchX = e.touches[0].clientX
    },
    onTouchMove(e) {
      if (this.galleryAsGrid) return

      const diffX = e.touches[0].clientX - this.initialTouchX

      if (Math.abs(diffX) > 30) {
        if (e.cancelable) e.preventDefault()
        this.diffX = diffX
      }
    },
    onTouchEnd(e) {
      if (this.galleryAsGrid) return
      if (Math.abs(this.diffX) > 30) {
        if (e.cancelable) e.preventDefault()
        if (this.diffX > 0) {
          this.currentIndex = Math.max(0, this.currentIndex - 1)
        } else {
          this.currentIndex = Math.min(
            this.slider.elements.length - 1,
            this.currentIndex + 1
          )
        }
      }

      this.diffX = 0
      this.initialTouchX = null
    },
    async onZoom(line, index) {
      if (this.$vssWidth < 769) {
        this.galleryAsGrid = false
        this.scrollToElement(index)
        return
      }

      if (
        this.galleryState.desktop.itemsPositions[index].type === 'one-per-line'
      ) {
        return
      }

      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
      const { Flip } = await import(
        'gsap/dist/Flip' /* webpackChunkName: 'gsap_flip' */
      )

      gsap.registerPlugin(Flip)

      if (this.zoomImage.flip === null) {
        this.zoomImage.flip = Flip.batch('gallery')
      } else {
        this.zoomImage.flip.clear()
      }

      const element = this.$refs.child[index]
      if (!element && !this.$refs.grid) {
        return
      }

      this.zoomImage.flip.add({
        getState: () => {
          return Flip.getState(this.$refs.grid)
        },
        setState: () => {
          if (this.zoomImage.index === index) {
            this.$refs.grid.style.gridTemplateRows = ''
          } else {
            this.$refs.grid.style.gridTemplateRows =
              this.getGalleryGridTemplateRows(
                this.galleryState.desktop.lines,
                this.gapDesktop,
                line - 1
              )
          }
        },
        animate: (self) =>
          Flip.from(self.state, {
            duration: 0.5,
            ease: 'power1.inOut',
            absolute: false,
          }),
      })

      if (this.zoomImage.index !== -1 && this.zoomImage.index !== index) {
        const prevElement = this.$refs.child[this.zoomImage.index]
        this.zoomImage.flip.add({
          getState: () => {
            return Flip.getState(prevElement.$el)
          },
          setState: () => {
            prevElement.$el.style.gridColumn = ''
            prevElement.$el.style.zIndex = ''
          },
          animate: (self) =>
            Flip.from(self.state, {
              duration: 0.5,
              ease: 'power1.inOut',
              absolute: true,
              scale: true,
            }),
          onComplete: () => {
            prevElement.$el.style.gridArea = ''
            this.scrollToElement(index)
          },
        })
      } else {
        this.scrollToElement(index)
      }

      this.zoomImage.flip.add({
        getState: () => {
          return Flip.getState(element.$el)
        },
        setState: () => {
          if (this.zoomImage.index === index) {
            element.$el.style.gridColumn = ''
          } else {
            element.$el.style.gridColumn = '1 / 13'
          }
          element.$el.style.zIndex = '2'
        },
        animate: (self) =>
          Flip.from(self.state, {
            duration: 0.5,
            ease: 'power1.inOut',
            absolute: true,
            scale: true,
          }),
        onComplete: () => {
          if (this.zoomImage.index === index) {
            this.zoomImage.index = -1
            element.$el.style.zIndex = ''

            this.$track.event('unzoom', {
              value: index,
            })
          } else {
            element.$el.style.gridArea = ''
            element.$el.style.gridColumn = '1 / 13'
            this.zoomImage.index = index

            this.$track.event('zoom', {
              value: index,
            })
          }
        },
      })

      this.zoomImage.flip.run()
    },
    toggleMobileGallery() {
      this.galleryAsGrid = !this.galleryAsGrid

      this.$track.event('switch-slider-gallery', {
        to: this.galleryAsGrid ? 'grid' : 'slider',
      })
    },
    async scrollToElement(index) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
      const { ScrollToPlugin } = await import(
        'gsap/ScrollToPlugin' /* webpackChunkName: 'gsap_scroll_to_plugin' */
      )
      gsap.registerPlugin(ScrollToPlugin)

      const element = this.$refs.child[index]
      if (!element) return

      this.setScroll(true)
      gsap.to(window, {
        duration: 0.3,
        scrollTo: { y: element.$el, offsetY: -1 },
        onComplete: () => {
          this.setScroll(false)
        },
      })
    },
    onColorChange() {
      this.currentIndex = 0
      this.zoomImage.index = -1
      this.$refs.grid.style.gridTemplateRows = ''

      if (this.zoomImage.flip !== null) {
        this.zoomImage.flip.clear()
      }
    },
  },
}
</script>

<style lang="scss">
.gallery {
  position: relative;

  @include mq($until: tablet) {
    overflow: hidden;
    max-width: 100%;
  }
}

.gallery__inner {
  @include mq($until: tablet) {
    display: flex;
    margin-left: var(--margin-left, 0);
    transition: margin 0.3s ease-out;
    touch-action: pan-y;

    &.gallery__inner--as-grid {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      grid-gap: var(--gap, 5px);
      grid-template-rows: var(--lines);
      margin-left: 0;
    }
  }

  @include mq($from: tablet) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: var(--gap-desktop, 5px);
    grid-template-rows: var(--lines-desktop);
  }
}

.gallery__page {
  position: absolute;
  bottom: var(--spacing);
  left: 0;
  right: 0;
  z-index: 1;

  .action {
    padding-left: var(--spacing);
    padding-right: var(--spacing);
  }
}

.gallery__video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.gallery__zoom {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 50%;
  visibility: hidden;
  background-color: blue;

  .lazy-image-container,
  .lazy-image {
    background: transparent;
  }
}
</style>
